import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Result, Card, Typography, Menu, Layout, Avatar, Spin, message } from 'antd';
import {
  UserOutlined,
  GiftOutlined,
  SmileOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
  PictureOutlined,
  NotificationOutlined,
  FlagOutlined,
  BarChartOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  CrownOutlined,
  // DashboardOutlined, // Dashboard page hidden
  SwapOutlined,
  LineChartOutlined,
  HomeOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  MailOutlined,
  TrophyOutlined,
  PieChartOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import RolesPage from './RolesPage';
import EcardsPage from './EcardsPage';
import RewardsPage from './RewardsPage';
import BudgetPage from './BudgetPage';
import ImagePages from './ImagePages';
import Communique from './Communique';
import RewardsFinalApprovalUpload from './RewardsFinalApprovalUpload';
import AutomateBudget from './AutomateBudget';
import useUserProfile from '../../useUserProfile';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import Banners from './Banners';
import FootFall from './FootFall';
import RewardTypeReport from './RewardTypeReport';
import BudgetReport from './BudgetReport';
import BudgetHistory from './BudgetHistory';
import UnitBudgetHistory from './UnitBudgetHistory';
import ReminderHistory from './ReminderHistory';
import VivaEngage from './VivaEngage';
import Help from './AdminHelp';
import AdminApproval from './AdminApproval';
import PointConversion from './PointConversion';
// import AdminDashboard from './AdminDashboard'; // Dashboard page hidden — see getTabItems
import AwardsInsights, { ADMIN_HEADER_ACTIONS_ID } from './AwardsInsights';
import AwardImageUpload from '../Organism/AwardImageUpload';
import ELTEmail from './ELTEmail';
import ACESuperAdmin from './ACESuperAdmin';
import AceAviaAwards from './AceAviaAwards';
import CampaignsMaster from './CampaignsMaster';
import apteanLogo from '../../assets/images/New_ApteanThanks_Logo.png';

const { Title, Paragraph } = Typography;
const { Sider, Content } = Layout;

interface UserDetailsType {
  id: string;
  displayName: string;
  email: string;
}

interface TabItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  content?: React.ReactNode;
  children?: TabItem[];
}

const getTabItems = (): TabItem[] => {
  // Insights pinned first; remaining items sorted alphabetically.
  // Dashboard page hidden for now — Insights Dashboard is the default landing page.
  // const dashboard: TabItem = { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined />, content: <AdminDashboard /> };
  const insights: TabItem = { key: 'insights', label: 'Insights Dashboard', icon: <PieChartOutlined />, content: <AwardsInsights /> };

  const rest: TabItem[] = [
    { key: 'aceAviaAwards',        label: 'ACE/AVIA Awards',          icon: <TrophyOutlined />,        content: <AceAviaAwards /> },
    { key: 'adminAwardsApproval',  label: 'Admin Awards Approval',    icon: <CrownOutlined />,         content: <AdminApproval /> },
    { key: 'automateBudget',       label: 'Automate Manager Budget',  icon: <ThunderboltOutlined />,   content: <AutomateBudget /> },
    { key: 'budget',               label: 'Budget',                   icon: <DollarOutlined />,        content: <BudgetPage /> },
    { key: 'ecards',               label: 'Ecards',                   icon: <SmileOutlined />,         content: <EcardsPage /> },
    {
      key: 'masters',
      label: 'Masters',
      icon: <AppstoreOutlined />,
      children: [
        { key: 'aceSuperAdmin',    label: 'ACE Super Admin',    icon: <CrownOutlined />,          content: <ACESuperAdmin /> },
        { key: 'awardFeedImages',  label: 'ACE/AVIA Images',   icon: <PictureOutlined />,        content: <AwardImageUpload /> },
        { key: 'banners',          label: 'Banners',            icon: <FlagOutlined />,           content: <Banners /> },
        { key: 'campaigns',        label: 'Campaigns',          icon: <CalendarOutlined />,       content: <CampaignsMaster /> },
        { key: 'communique',       label: 'Communique',         icon: <NotificationOutlined />,   content: <Communique /> },
        { key: 'eltEmail',         label: 'ELT Emails (ELT Calibration)', icon: <MailOutlined />, content: <ELTEmail /> },
        { key: 'help',             label: 'Help',               icon: <QuestionCircleOutlined />, content: <Help /> },
        { key: 'images',           label: 'Images',             icon: <PictureOutlined />,        content: <ImagePages /> },
        { key: 'pointConversion',  label: 'Point Conversion',   icon: <SwapOutlined />,           content: <PointConversion /> },
      ],
    },
    {
      key: 'report',
      label: 'Reports',
      icon: <BarChartOutlined />,
      children: [
        { key: 'budgetHistory',     label: 'Budget History',      icon: <HistoryOutlined />,   content: <BudgetHistory /> },
        { key: 'budgetReport',      label: 'Budget Report',       icon: <DollarOutlined />,    content: <BudgetReport /> },
        { key: 'footfall',          label: 'Footfall Report',     icon: <LineChartOutlined />, content: <FootFall /> },
        { key: 'reminderHistory',   label: 'Reminder History',    icon: <MailOutlined />,      content: <ReminderHistory /> },
        { key: 'rewardTypeReport',  label: 'Reward Type Report',  icon: <GiftOutlined />,      content: <RewardTypeReport /> },
        { key: 'unitBudgetHistory', label: 'Unit Budget History', icon: <HistoryOutlined />,   content: <UnitBudgetHistory /> },
        { key: 'vivaEngage',        label: 'Viva Engage',         icon: <TeamOutlined />,      content: <VivaEngage /> },
      ],
    },
    { key: 'rewards',              label: 'Rewards',                  icon: <GiftOutlined />,          content: <RewardsPage /> },
    { key: 'rewardsApproval',      label: 'Rewards Admin Approval',   icon: <CheckCircleOutlined />,   content: <RewardsFinalApprovalUpload /> },
    { key: 'roles',                label: 'Roles',                    icon: <UserOutlined />,          content: <RolesPage /> },
  ];

  return [/* dashboard, */ insights, ...rest];
};

const AdminPages: React.FC = () => {
  const [userDetails, setUserDetails] = useState<UserDetailsType | null>(null);
  // Internal DB user id (UUID) resolved from the signed-in user's email — the source of
  // truth for the admin check. null = still resolving (keep the spinner up); '' = resolved
  // but unknown (treat as non-admin instead of hanging).
  const [internalUserId, setInternalUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState('insights');
  const [error, setError] = useState<string | null>(null);
  const { requestProfileData } = useUserProfile();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  // Memoize tab items for performance
  const tabItems = useMemo(getTabItems, []);

  // Fetch token
  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  }, [instance, accounts]);

  // Resolve the current user for display + the internal DB user id used by the admin check.
  // The isAdmin API needs the internal DB UUID (from /api/Users/CheckEmail), NOT the Azure AD
  // object id. We resolve it from the email so it no longer depends on whatever happens to be
  // in localStorage.UserId at mount time — the historical cause of the "403 on first click,
  // works after refresh" bug, where the key briefly held the Azure object id.
  useEffect(() => {
    let cancelled = false;

    // Optimistic display from cache so the header isn't blank while we resolve.
    const cachedName = localStorage.getItem("UserName");
    const cachedMail = localStorage.getItem("UserMail");
    if (cachedName || cachedMail) {
      setUserDetails({ id: '', displayName: cachedName || 'Admin', email: cachedMail || '' });
    }

    const resolveUser = async () => {
      try {
        // Refresh mail/name (no longer clobbers the internal UserId).
        await requestProfileData();
        const email = (localStorage.getItem("UserMail") || '').toLowerCase();
        const name = localStorage.getItem("UserName") || 'Admin';

        // Resolve the internal DB user id from the email.
        let resolvedId = '';
        if (email && accounts[0]) {
          try {
            const token = await fetchToken();
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/CheckEmail`, {
              params: { email },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.data?.userId) {
              resolvedId = res.data.userId;
              localStorage.setItem("UserId", resolvedId);
            }
          } catch (e) {
            // Fall back to any internal id already cached from a prior page.
          }
        }
        if (!resolvedId) {
          resolvedId = localStorage.getItem("UserId") || '';
        }

        if (!cancelled) {
          setUserDetails({ id: resolvedId, displayName: name, email });
          setInternalUserId(resolvedId); // '' => resolved-but-unknown => non-admin (no hang)
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to fetch user profile.');
          setInternalUserId('');
        }
      }
    };

    resolveUser();
    return () => { cancelled = true; };
  }, [requestProfileData, fetchToken, accounts]);

  // Check admin status once the internal user id has been resolved.
  useEffect(() => {
    if (internalUserId === null) return; // still resolving
    if (!internalUserId) { setIsAdmin(false); setLoading(false); return; }

    let cancelled = false;
    const checkAdmin = async () => {
      try {
        const token = await fetchToken();
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Roles/isAdmin`, {
          params: { userId: internalUserId },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        });
        if (!cancelled) setIsAdmin(response.data);
      } catch (err) {
        if (!cancelled) setError('Failed to verify admin status.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    checkAdmin();
    return () => { cancelled = true; };
  }, [internalUserId, fetchToken]);

  // Handle menu click
  const handleMenuClick = useCallback((e: any) => {
    setSelectedKey(e.key);
  }, []);

  // Responsive Sider
  const [collapsed, setCollapsed] = useState(false);

  // Error feedback
  useEffect(() => {
    if (error) message.error(error);
  }, [error]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" tip="Loading admin dashboard..." />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="Sorry, you do not have access to this page."
      />
    );
  }

  const selectedTab = tabItems.find(tab =>
    tab.key === selectedKey || tab.children?.some(child => child.key === selectedKey)
  );
  const selectedContent =
    selectedTab?.content ||
    selectedTab?.children?.find(child => child.key === selectedKey)?.content;

  const initials = (userDetails?.displayName || 'Admin')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <style>{`
        /* ── Sidebar menu items (vertical mode) ── */
        .admin-sider-menu.ant-menu-vertical .ant-menu-item,
        .admin-sider-menu.ant-menu-vertical .ant-menu-submenu-title {
          border-radius: 8px;
          margin: 2px 10px;
          width: calc(100% - 20px);
          padding-left: 14px !important;
          font-size: 13.5px;
          font-weight: 500;
          color: #444;
          transition: background 0.2s, color 0.2s;
        }
        .admin-sider-menu.ant-menu-vertical .ant-menu-item:hover,
        .admin-sider-menu.ant-menu-vertical .ant-menu-submenu-title:hover,
        .admin-sider-menu.ant-menu-vertical .ant-menu-submenu-open > .ant-menu-submenu-title {
          background: rgba(134, 41, 164, 0.08) !important;
          color: #8629A4 !important;
        }
        .admin-sider-menu .ant-menu-item-selected {
          background: rgba(134, 41, 164, 0.12) !important;
          color: #8629A4 !important;
          font-weight: 700;
          border-left: 3px solid #8629A4;
        }
        .admin-sider-menu .ant-menu-item-selected::after {
          display: none;
        }
        .admin-sider-menu .ant-menu-item-selected .anticon,
        .admin-sider-menu .ant-menu-item:hover .anticon,
        .admin-sider-menu .ant-menu-submenu-title:hover .anticon,
        .admin-sider-menu .ant-menu-submenu-open > .ant-menu-submenu-title .anticon {
          color: #8629A4;
        }
        /* collapsed icon-only state */
        .admin-sider-menu.ant-menu-inline-collapsed .ant-menu-item,
        .admin-sider-menu.ant-menu-inline-collapsed .ant-menu-submenu-title {
          padding-left: 0 !important;
          margin: 2px 6px;
          width: calc(100% - 12px);
          text-align: center;
        }
        /* ── Reports popup submenu ── */
        .admin-submenu-popup .ant-menu-item {
          font-size: 13.5px;
          font-weight: 500;
          color: #444;
          border-radius: 6px;
          margin: 2px 6px;
          width: calc(100% - 12px);
          transition: background 0.2s, color 0.2s;
        }
        .admin-submenu-popup .ant-menu-item:hover {
          background: rgba(134, 41, 164, 0.1) !important;
          color: #8629A4 !important;
        }
        .admin-submenu-popup .ant-menu-item-selected {
          background: rgba(134, 41, 164, 0.14) !important;
          color: #8629A4 !important;
          font-weight: 700;
        }
        .admin-submenu-popup.ant-menu-submenu-popup > .ant-menu {
          border-radius: 10px;
          box-shadow: 0 6px 24px rgba(134,41,164,0.15);
          border: 1px solid rgba(134,41,164,0.1);
          padding: 6px 0;
          min-width: 200px;
        }
        .admin-nav-home-btn:hover {
          background: rgba(134, 41, 164, 0.06) !important;
        }
      `}</style>

      <Sider
        width={248}
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        style={{
          background: '#fff',
          boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
          position: 'sticky',
          top: 0,
          height: '100vh',
          zIndex: 10,
          overflow: 'hidden',
        }}
        breakpoint="lg"
        collapsedWidth={64}
        aria-label="Admin navigation"
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* ── Header strip ── */}
          <div style={{
            background: 'linear-gradient(90deg, #8629A4 0%, #E69CFF 100%)',
            padding: collapsed ? '14px 0' : '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(134,41,164,0.25)',
          }}>
            <img
              src={apteanLogo}
              alt="Aptean Thanks"
              style={{
                height: collapsed ? 32 : 38,
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'brightness(0) invert(1)',
              }}
            />
            {!collapsed && (
              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.4)', paddingLeft: 10 }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 13.5, lineHeight: 1.2, letterSpacing: 0.2 }}>Admin Panel</div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10.5, marginTop: 2, letterSpacing: 0.3 }}>Aptean Thanks Portal</div>
              </div>
            )}
          </div>

          {/* ── User profile ── */}
          <div style={{
            padding: collapsed ? '14px 0' : '14px 16px',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
            background: '#fafafa',
          }}>
            <Avatar
              size={36}
              style={{
                background: 'linear-gradient(135deg, #8629A4, #E69CFF)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>
            {!collapsed && (
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {userDetails?.displayName || 'Admin'}
                </div>
                <div style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {userDetails?.email}
                </div>
              </div>
            )}
          </div>

          {/* ── Menu ── */}
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingTop: 8, paddingBottom: 8 }}>
            <Menu
              className="admin-sider-menu"
              mode="vertical"
              selectedKeys={[selectedKey]}
              onClick={handleMenuClick}
              style={{ borderRight: 0 }}
              aria-label="Admin menu"
            >
              {tabItems.map(tab =>
                tab.children ? (
                  <Menu.SubMenu
                    key={tab.key}
                    icon={tab.icon}
                    title={tab.label}
                    popupClassName="admin-submenu-popup"
                    popupOffset={[4, -8]}
                  >
                    {tab.children.map(child => (
                      <Menu.Item key={child.key} icon={child.icon}>
                        {child.label}
                      </Menu.Item>
                    ))}
                  </Menu.SubMenu>
                ) : (
                  <Menu.Item key={tab.key} icon={tab.icon}>
                    {tab.label}
                  </Menu.Item>
                )
              )}
            </Menu>
          </div>

          {/* ── Home button ── */}
          <div
            className="admin-nav-home-btn"
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: collapsed ? '14px 0' : '12px 20px',
              cursor: 'pointer',
              borderTop: '1px solid #f0f0f0',
              flexShrink: 0,
              justifyContent: collapsed ? 'center' : 'flex-start',
              transition: 'background 0.2s',
            }}
            title="Go to Home"
          >
            <HomeOutlined style={{ fontSize: 18, color: '#8629A4' }} />
            {!collapsed && (
              <span style={{ fontSize: 13, fontWeight: 500, color: '#8629A4' }}>Back to Home</span>
            )}
          </div>

        </div>
      </Sider>

      <Layout>
        <Content style={{ padding: '16px', maxWidth: 1600, margin: '0 auto', width: '100%' }}>
          <Card
            style={{
              minHeight: 600,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              borderRadius: 12,
              background: '#fff',
            }}
            bodyStyle={{}}
          >
            {selectedKey !== 'pointConversion' && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <Title level={2} style={{ marginBottom: 0, marginTop: -10, color: '#8629A4' }}>
                  {selectedTab?.children?.find(child => child.key === selectedKey)?.label || selectedTab?.label || 'Admin Dashboard'}
                </Title>
                {/* Pages portal their header actions (e.g. Insights' Export) into this slot. */}
                <div id={ADMIN_HEADER_ACTIONS_ID} style={{ marginTop: -10 }} />
              </div>
            )}
            <div style={{ marginTop: selectedKey !== 'pointConversion' ? 25 : 0 }}>
              {selectedContent}
            </div>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminPages;
